import { useEffect, useState } from "react";
import { Delete, X } from "lucide-react";
import { formatMoney } from "@/lib/format";

/**
 * Bottom-sheet modal for entering an amount + optional description / tag.
 * Used for both MAKE and SPEND flows.
 */
export default function AmountEntryModal({ open, mode, onClose, onConfirm }) {
  const [amountStr, setAmountStr] = useState("");
  const [description, setDescription] = useState("");
  const [tag, setTag] = useState(null); // 'cash' | 'venmo'
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAmountStr("");
      setDescription("");
      setTag(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const isMake = mode === "make";
  const accent = isMake ? "#7CE645" : "#FF5A36";
  const title = isMake ? "MAKE MONEY!" : "SPEND MONEY";
  const subtitle = isMake ? "How much did you earn?" : "How much did you spend?";

  const pressKey = (key) => {
    setAmountStr((prev) => {
      if (key === "del") return prev.slice(0, -1);
      if (key === ".") {
        if (prev.includes(".")) return prev;
        if (prev === "") return "0.";
        return prev + ".";
      }
      // digit
      if (prev === "0") return key;
      // limit to 2 decimal places
      if (prev.includes(".")) {
        const [, dec] = prev.split(".");
        if (dec && dec.length >= 2) return prev;
      }
      // hard cap on length
      if (prev.length >= 8) return prev;
      return prev + key;
    });
  };

  const amount = parseFloat(amountStr || "0");
  const canSubmit =
    amount > 0 && !submitting && (isMake || description.trim().length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onConfirm({
        type: mode,
        amount,
        description: description.trim(),
        tag: isMake ? tag : null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={`amount-entry-modal-${mode}`}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center fade-in"
      style={{ backgroundColor: "rgba(42, 45, 124, 0.45)" }}
      onClick={onClose}
    >
      <div
        className="modal-slide-up w-full sm:max-w-md bg-white rounded-t-[36px] sm:rounded-[36px] border-4 border-navy p-5 sm:p-7 max-h-[95vh] overflow-y-auto"
        style={{ boxShadow: "0 -8px 0 0 #2A2D7C" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-3xl sm:text-4xl text-navy">{title}</h2>
          <button
            data-testid="close-modal-btn"
            onClick={onClose}
            className="w-10 h-10 rounded-full border-3 border-navy flex items-center justify-center bg-white hover:scale-105 transition-transform"
            style={{ boxShadow: "0 3px 0 0 #2A2D7C", borderWidth: 3 }}
            aria-label="Close"
          >
            <X className="w-5 h-5 text-navy" strokeWidth={3} />
          </button>
        </div>
        <p className="font-display text-base text-navy/70 mb-4">{subtitle}</p>

        {/* Amount display */}
        <div
          className="rounded-3xl border-4 border-navy p-5 mb-4 text-center"
          style={{ backgroundColor: accent, boxShadow: "0 4px 0 0 #2A2D7C" }}
        >
          <div
            data-testid="amount-display"
            className="font-display text-5xl sm:text-6xl text-navy break-all"
          >
            {amountStr === "" ? "$0" : `$${amountStr}`}
          </div>
        </div>

        {/* What you bought (only spend) */}
        {!isMake && (
          <div className="mb-4">
            <label className="font-display text-base text-navy block mb-2">
              What did you buy?
            </label>
            <input
              data-testid="spend-description-input"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. candy, toy car..."
              className="w-full font-display text-xl text-navy rounded-2xl border-4 border-navy px-4 py-3 outline-none focus:ring-0"
              style={{ boxShadow: "0 4px 0 0 #2A2D7C" }}
              maxLength={60}
            />
          </div>
        )}

        {/* Tag (only make) */}
        {isMake && (
          <div className="mb-4">
            <label className="font-display text-base text-navy block mb-2">
              How did you get paid? (optional)
            </label>
            <div className="flex gap-3">
              <TagButton
                label="CASH"
                active={tag === "cash"}
                color="#7CE645"
                onClick={() => setTag(tag === "cash" ? null : "cash")}
                testId="tag-cash"
              />
              <TagButton
                label="VENMO"
                active={tag === "venmo"}
                color="#29C7E8"
                onClick={() => setTag(tag === "venmo" ? null : "venmo")}
                testId="tag-venmo"
              />
            </div>
          </div>
        )}

        {/* Number pad */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button
              key={n}
              data-testid={`numpad-${n}`}
              onClick={() => pressKey(String(n))}
              className="numpad-key"
            >
              {n}
            </button>
          ))}
          <button
            data-testid="numpad-dot"
            onClick={() => pressKey(".")}
            className="numpad-key"
          >
            .
          </button>
          <button
            data-testid="numpad-0"
            onClick={() => pressKey("0")}
            className="numpad-key"
          >
            0
          </button>
          <button
            data-testid="numpad-del"
            onClick={() => pressKey("del")}
            className="numpad-key flex items-center justify-center"
            aria-label="delete"
          >
            <Delete className="w-7 h-7" strokeWidth={3} />
          </button>
        </div>

        {/* Confirm button */}
        <button
          data-testid="confirm-amount-btn"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="chunky-btn w-full py-4 text-2xl sm:text-3xl text-navy disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: accent }}
        >
          {submitting ? "SAVING..." : isMake ? `ADD ${formatMoney(amount)}` : `SPEND ${formatMoney(amount)}`}
        </button>
      </div>
    </div>
  );
}

function TagButton({ label, active, color, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="flex-1 chunky-btn py-3 text-lg sm:text-xl"
      style={{
        backgroundColor: active ? color : "white",
        opacity: active ? 1 : 0.85,
      }}
    >
      {label}
    </button>
  );
}
